import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';

import {
  AudioLogoColorSVG,
  AudioLogoSVG,
  AudioLogoTypesSVG,
  CreateACampaignLogoSVG,
  CTVLogoColorSVG,
  CTVLogoSVG,
  CTVLogoTypesSVG,
  DataStudioSourceLogo,
  DisplayLogoColorSVG,
  DisplayLogoSVG,
  DisplayLogoTypesSVG,
  GettingStartedLogoSVG,
  LinkedInLogoSVG,
  NativeAdLogoSVG,
  NewBuilderLogoSvg,
  RetargetingLogoColorSVG,
  RetargetingLogoSVG,
  RetargetingLogoTypesSVG,
  VideoLogoColorSVG,
  VideoLogoSVG,
  VideoLogoTypesSVG,
} from '@shared-assets/images';
import { LinkComponent } from '@terminus-ui/link';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { SvgComponent } from '@terminus-ui/svg';
import { EntitlementsService } from 'entitlements';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { firstValueFrom, map, Observable } from 'rxjs';
import { SplitIoService } from 'split-io';

export type CampaignType =
  | 'display'
  | 'video'
  | 'ctv'
  | 'retargeting'
  | 'audio'
  | 'native'
  | 'manually_managed'
  | 'linkedin'
  | undefined;

/**
 * Welcome screen for users to select the type of campaign they wish to create
 */
@Component({
  selector: 'frontend-create-a-campaign',
  templateUrl: './create-a-campaign.component.html',
  styleUrls: ['./create-a-campaign.component.scss'],
  imports: [
    CommonModule,
    PageHeaderComponent,
    SvgComponent,
    CardModule,
    LinkComponent,
    PageHeaderButtonsComponent,
    ButtonModule,
    FontAwesomeModule,
    TagModule,
  ],
})
export class CreateACampaignComponent {
  router = inject(Router);

  sparklesIcon = faSparkles;
  displayLogo = DisplayLogoSVG;
  displayLogoColor = DisplayLogoColorSVG;
  displayLogoTypes = DisplayLogoTypesSVG;
  videoLogo = VideoLogoSVG;
  videoLogoColor = VideoLogoColorSVG;
  videoLogoTypes = VideoLogoTypesSVG;
  ctvLogo = CTVLogoSVG;
  ctvLogoColor = CTVLogoColorSVG;
  ctvLogoTypes = CTVLogoTypesSVG;
  retargetingLogo = RetargetingLogoSVG;
  retargetingLogoColor = RetargetingLogoColorSVG;
  retargetingLogoTypes = RetargetingLogoTypesSVG;
  audioLogo = AudioLogoSVG;
  audioLogoColor = AudioLogoColorSVG;
  audioLogoTypes = AudioLogoTypesSVG;
  nativeLogo = NativeAdLogoSVG;
  createACampaignLogo = CreateACampaignLogoSVG;
  linkedInLogo = LinkedInLogoSVG;
  newBuilderLogo = NewBuilderLogoSvg;
  gettingStartedLogo = GettingStartedLogoSVG;
  dataStudioSourceLogo = DataStudioSourceLogo;

  selectedCampaignType: CampaignType;

  entitleService = inject(EntitlementsService);

  breadcrumbs = [
    { path: '', title: 'Home' },
    { path: '/create-a-campaign', title: 'Create a Campaign' },
  ];

  ctvVideoFlag$: Observable<boolean> = inject(SplitIoService)
    .getSplitTreatment$('adx_video_ctv_enabled')
    .pipe(map((treatment) => (treatment === 'on' ? true : false)));

  audioFlag$: Observable<boolean> = inject(SplitIoService)
    .getSplitTreatment$('adx_audio_enabled')
    .pipe(map((treatment) => (treatment === 'on' ? true : false)));

  isAdmin$ = this.entitleService
    .getEntitlement$({
      requireMyTAdmin: true,
    })
    .pipe(map((isAdmin) => isAdmin === true));

  async preventBlur($blurEvent: any) {
    // video & ctv disabled if flag isn't set
    let ctvVideoFlag = await firstValueFrom(this.ctvVideoFlag$);
    let audioFlag = await firstValueFrom(this.ctvVideoFlag$);
    if (
      !$blurEvent.relatedTarget ||
      (['video', 'ctv'].includes($blurEvent.relatedTarget.id) &&
        !ctvVideoFlag) ||
      (['audio'].includes($blurEvent.relatedTarget.id) && !audioFlag) ||
      !['P-CARD', 'BUTTON'].includes($blurEvent.relatedTarget.tagName)
    ) {
      $blurEvent.preventDefault();
      $blurEvent.stopPropagation();
      $blurEvent.currentTarget.focus();
    }
  }

  cancelEvent($event: any) {
    $event.preventDefault();
    $event.stopPropagation();
    $event.target.blur();
  }

  setSelectedCampaignType(type: CampaignType) {
    this.selectedCampaignType = type;
  }

  createCampaign(classic: boolean) {
    switch (this.selectedCampaignType) {
      case 'display':
        this.router.navigate(
          classic
            ? ['/adex/display-tactic-wizard/draft/create']
            : ['/adex/multi-campaign-creation-wizard', { type: 'display' }],
        );
        break;
      case 'video':
        this.router.navigate([
          '/adex/multi-campaign-creation-wizard',
          { type: 'video' },
        ]);
        break;
      case 'ctv':
        this.router.navigate([
          '/adex/multi-campaign-creation-wizard',
          { type: 'ctv' },
        ]);
        break;
      case 'retargeting':
        this.router.navigate(['/adex/tactic-wizard/retargeting']);
        break;
      case 'linkedin':
        this.router.navigate(['/adex/linkedin-audience/new']);
        break;
      case 'audio':
        this.router.navigate([
          '/adex/multi-campaign-creation-wizard',
          { type: 'audio' },
        ]);
        break;
      case 'manually_managed':
        this.router.navigate([
          '/adex/multi-campaign-creation-wizard',
          { type: 'manually_managed' },
        ]);
        break;
      case 'native':
        break;
      case undefined:
        break;
    }
  }
}
